import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from 'primereact/card'
import { Column as TableColumn } from 'primereact/column'
import { DataTable, DataTableRowClickEvent } from 'primereact/datatable'

import { PermissionSuffix } from '../../_generated/api/user-management'
import { BoltConstraintsDto } from '../../_generated/api/structure'
import { useArchiveEntryAndNavigate } from '../../hooks/crud'
import Bolt from '../../models/Bolt'
import MenuBuilder from '../../tools/MenuBuilder'
import { toDecimalPlaces, mm } from '../../tools/format'
import { buildNavigationLink } from '../../tools/link'
import { StructureTypes } from '../../models/Structure'
import { BoltDiameterSettings, BoltTypeSettings, MaterialSettings } from '../../tools/settings'
import t, { _ta, _tf } from '../../tools/t'
import { useBoltContext } from '../contexts/BoltContext'
import { useBoltConnectionContext } from '../contexts/BoltConnectionContext'
import { useExportContext } from '../contexts/ExportContext'
import { useStructureContext } from '../contexts/StructureContext'
import { useUserContext } from '../contexts/UserContext'
import { TooltipAvatar } from '../common/ProfileAvatar'
import DateDisplay from '../common/pure/DateDisplay'
import { Column, Row } from '../common/pure/FlexContainer'
import FullScreenLoading from '../common/FullScreenLoading'
import { CalendarField, DropdownField, NumberField } from '../common/pure/input-fields'
import { ContentColumn, LayoutRowContainer } from '../common/pure/layout'
import { getMenuItemArchive, getMenuItemExport, getMenuItemSeparator } from '../common/menu-items'
import PrimeIcon from '../common/pure/PrimeIcon'
import { ErrorMessageScreen } from '../common/pure/MessageScreen'
import {
    BoltMeasurementProgressTooltipIcon,
    BoltMeasurementProgressTooltipRow,
    TooltipIcon,
} from '../common/pure/TooltipWrapper'
import StructureScreen from './StructureScreen'
import { TagIdCopyButton } from '../common/pure/CopyButton'
import PretensionGraph from '../graph/PretensionGraph'
import TablePagination from '../table/TablePagination'
import BoltConnectionServiceWindow from '../common/BoltConnectionServiceWindow'
import {
    ArchiveButton,
    RestoreButton,
    TableActionContainer,
    UnlinkButton,
} from '../table/TableButton'
import {
    getFilterableTableProps,
    getSortableColumnProps,
    getSortableTableProps,
} from '../table/table-props'

export default function BoltConnectionScreen() {
    const navigate = useNavigate()
    const {
        loading,
        bolts,
        allBolts,
        selectedBoltConnection,
        selectedBoltConnectionLoading,
        selectedBoltConnectionError,
        qr,
        refreshAllBolts,
        refreshBolts,
        refreshSelectedBoltConnection,
        unassignBolt,
        updateBoltConnection,
        boltDetails,
        pretensionDateRange,
        setPretensionDateRange,
    } = useBoltConnectionContext()
    const { archiveBolt, restoreBolt } = useBoltContext()
    const { exportBoltConnection } = useExportContext()
    const { restoreBoltConnection, structure, structureLoading } = useStructureContext()
    const { can } = useUserContext()
    const archiveAndNavigate = useArchiveEntryAndNavigate()
    const canWriteStructure = can(PermissionSuffix.StructureWrite)
    const canWriteHardware = can(PermissionSuffix.HardwareWrite)

    const [editableData, setEditableData] = useState<BoltConstraintsDto>()
    const [hasChanged, setHasChanged] = useState(false)

    useEffect(() => {
        const data = selectedBoltConnection?.constraints
        if (data) setEditableData(data)
    }, [selectedBoltConnection])

    const isArchived = !!selectedBoltConnection?.isArchived

    const changeData = (change: BoltConstraintsDto) => {
        if (isArchived || !editableData) return
        setEditableData({ ...editableData, ...change })
        setHasChanged(true)
    }

    const submitNameChange = async (name: string) => {
        if (isArchived) return false
        const boltConnection = await updateBoltConnection({ name })
        return !!boltConnection
    }

    const submitChange = async () => {
        if (isArchived || !(editableData && selectedBoltConnection)) return false
        const boltConnection = await updateBoltConnection({
            name: selectedBoltConnection.name,
            boltConstraints: editableData,
        })
        const success = !!boltConnection
        if (success) setHasChanged(false)
        return success
    }

    const archiveBoltConnectionAndNavigate = async () => {
        if (!selectedBoltConnection) return
        await archiveAndNavigate(selectedBoltConnection, StructureTypes.BoltConnection)
    }

    const onRestoreBoltConnection = async () => {
        if (!selectedBoltConnection) return
        await restoreBoltConnection(selectedBoltConnection.id)
        await refreshSelectedBoltConnection()
    }

    const onArchiveBolt = async (bolt: Bolt) => {
        if (isArchived) return
        if (!confirm('Are you sure you want to archive bolt:\n' + bolt.name)) return
        await archiveBolt(bolt.id)
        await refreshAllBolts()
        await refreshBolts()
    }

    const onRestoreBolt = async (bolt: Bolt) => {
        await restoreBolt(bolt.id)
        await refreshAllBolts()
        await refreshBolts()
    }

    const onUnassignBolt = async (bolt: Bolt) => {
        if (isArchived) return
        await unassignBolt(bolt)
    }

    const selectBolt = (bolt: Bolt) => navigate(bolt.referenceLink)

    const navigateToBolt = (event: DataTableRowClickEvent) => {
        const bolt = event.data as Bolt
        const link = buildNavigationLink(StructureTypes.Bolt, bolt.id)
        navigate(link)
    }

    const characteristicPretension = boltDetails?.characteristicPretensionForceKn ?? 0
    const minPretension = selectedBoltConnection?.characteristicPretensionForceMin ?? 0
    const averagePretension = boltDetails?.averagePretensionForceKn ?? 0
    const calculatedBolts = boltDetails?.numberOfBoltsInCalculation ?? 0
    const totalBolts = boltDetails?.totalNumberOfBolts ?? 0

    if (!selectedBoltConnection) {
        if (selectedBoltConnectionLoading) return <FullScreenLoading />
        return (
            <ErrorMessageScreen
                title={selectedBoltConnectionError?.name}
                message={selectedBoltConnectionError?.message}
            />
        )
    }

    const selectedEntry = structure.findEntry(selectedBoltConnection.id)
    if (!selectedEntry) {
        if (structureLoading) return <FullScreenLoading />
        return <ErrorMessageScreen />
    }
    const parent = selectedEntry.parentId ? structure.findEntry(selectedEntry.parentId) : undefined

    const getBoltDiscrepancies = (bolt: Bolt) => {
        if (!selectedBoltConnection) return []
        const { expectedBoltMaterial, expectedBoltSize, expectedBoltType } = selectedBoltConnection
        const { material, diameter, type } = bolt
        const discrepancies = []
        if (expectedBoltMaterial && material !== expectedBoltMaterial)
            discrepancies.push(
                t(
                    'bolt_field_discrepancy_material',
                    _tf('', 'material', bolt.material),
                    _tf('', 'material', expectedBoltMaterial)
                )
            )
        if (expectedBoltSize && diameter !== expectedBoltSize)
            discrepancies.push(
                t(
                    'bolt_field_discrepancy_size',
                    _ta(bolt, 'diameter'),
                    _ta(bolt, 'diameter', expectedBoltSize)
                )
            )
        if (expectedBoltType && type !== expectedBoltType)
            discrepancies.push(
                t(
                    'bolt_field_discrepancy_type',
                    _ta(bolt, 'type'),
                    _ta(bolt, 'type', expectedBoltType)
                )
            )

        return discrepancies
    }

    return (
        <StructureScreen
            node={selectedBoltConnection}
            parent={parent}
            ancestors={selectedBoltConnection?.ancestors ?? []}
            onSubmitNameChange={canWriteStructure ? submitNameChange : undefined}
            onSaveChanges={canWriteStructure ? submitChange : undefined}
            onRestore={canWriteStructure ? onRestoreBoltConnection : undefined}
            nodeHasChanged={hasChanged}
            qr={qr}
            menu={new MenuBuilder()
                .add(
                    getMenuItemExport(
                        () => exportBoltConnection(selectedBoltConnection?.id),
                        t('bolt_connection_export')
                    )
                )
                .addIf(getMenuItemSeparator(), canWriteStructure && !isArchived)
                .addIf(
                    getMenuItemArchive(
                        archiveBoltConnectionAndNavigate,
                        t('bolt_connection_delete')
                    ),
                    canWriteStructure && !isArchived
                )
                .toArray()}
        >
            <BoltConnectionServiceWindow key={selectedBoltConnection.id} archived={isArchived} />
            <LayoutRowContainer grow={false}>
                <ContentColumn columns={1}>
                    <Card title={t('bolt_connection_expected_constraints')}>
                        <Column gap={0.75}>
                            <Row gap={0.75}>
                                <NumberField
                                    placeholder={t('pretension_min')}
                                    allowDecimals
                                    value={editableData?.pretensionForceMinKn}
                                    unit={t('common_unit_kn')}
                                    disabled={!canWriteStructure || isArchived}
                                    onChange={(event) =>
                                        changeData({ pretensionForceMinKn: event.value ?? 0 })
                                    }
                                />

                                <NumberField
                                    placeholder={t('pretension_max')}
                                    allowDecimals
                                    value={editableData?.pretensionForceMaxKn}
                                    unit={t('common_unit_kn')}
                                    disabled={!canWriteStructure || isArchived}
                                    onChange={(event) =>
                                        changeData({
                                            pretensionForceMaxKn: event.value ?? 0,
                                        })
                                    }
                                />
                            </Row>

                            <NumberField
                                placeholder={t('characteristic_pretension_min')}
                                allowDecimals
                                value={editableData?.characteristicPretensionForceMinKn}
                                unit={t('common_unit_kn')}
                                disabled={!canWriteStructure || isArchived}
                                onChange={(event) =>
                                    changeData({
                                        characteristicPretensionForceMinKn: event.value ?? 0,
                                    })
                                }
                            />

                            <NumberField
                                placeholder={t('linear_force_constant')}
                                allowDecimals
                                value={editableData?.springConstantKnMm}
                                unit={t('common_unit_kn_mm')}
                                disabled={!canWriteStructure || isArchived}
                                onChange={(event) =>
                                    changeData({ springConstantKnMm: event.value ?? 0 })
                                }
                            />
                        </Column>
                    </Card>
                </ContentColumn>
                <ContentColumn columns={1}>
                    <Card title={t('common_details')}>
                        <Column gap={0.75}>
                            <NumberField
                                placeholder={t('expected_number_of_bolts')}
                                value={editableData?.expectedNumberOfBolts}
                                disabled={!canWriteStructure || isArchived}
                                onChange={(event) =>
                                    changeData({
                                        expectedNumberOfBolts: event.value,
                                    })
                                }
                            />

                            <Row gap={0.75}>
                                <DropdownField
                                    placeholder={t('expected_bolt_material')}
                                    value={editableData?.expectedBoltMaterial}
                                    options={MaterialSettings}
                                    disabled={!canWriteStructure || isArchived}
                                    onChange={(event) =>
                                        changeData({ expectedBoltMaterial: event.value })
                                    }
                                />

                                <DropdownField
                                    placeholder={t('expected_bolt_size')}
                                    value={editableData?.expectedBoltSize}
                                    options={BoltDiameterSettings}
                                    disabled={!canWriteStructure || isArchived}
                                    onChange={(event) =>
                                        changeData({ expectedBoltSize: event.value })
                                    }
                                />
                            </Row>
                            <Row gap={0.75}>
                                <DropdownField
                                    placeholder={t('expected_bolt_type')}
                                    value={editableData?.expectedBoltType}
                                    options={BoltTypeSettings}
                                    disabled={!canWriteStructure || isArchived}
                                    onChange={(event) =>
                                        changeData({ expectedBoltType: event.value })
                                    }
                                />
                                <NumberField
                                    placeholder={t('expected_nominal_length')}
                                    allowDecimals
                                    value={editableData?.expectedNominalLengthMm}
                                    disabled={!canWriteStructure || isArchived}
                                    onChange={(event) =>
                                        changeData({
                                            expectedNominalLengthMm: event.value ?? 0,
                                        })
                                    }
                                />
                            </Row>
                        </Column>
                    </Card>
                </ContentColumn>
                <ContentColumn columns={1}>
                    <Card
                        className="characteristic-pretension"
                        title={t('characteristic_pretension')}
                        subTitle={t('characteristic_pretension_subtitle')}
                    >
                        <Column arrangement="spaced" tall>
                            <CalendarField
                                value={pretensionDateRange.filter((d) => !!d)}
                                placeholder={t('characteristic_pretension_date_placeholder')}
                                selectionMode="range"
                                onChange={({ value }) =>
                                    setPretensionDateRange((value as Date[]) ?? [])
                                }
                            />

                            <Column gap={1} wide arrangement="spaced" alignment="center">
                                <Row
                                    className="characteristic-pretension__value"
                                    arrangement="center"
                                    alignment="end"
                                    gap={0.25}
                                >
                                    {toDecimalPlaces(characteristicPretension)}{' '}
                                    <div className="characteristic-pretension__unit">
                                        {t('common_unit_kn')}
                                    </div>
                                </Row>
                                <Row
                                    wide
                                    className="characteristic-pretension__results"
                                    arrangement="spaced"
                                    alignment="center"
                                    gap={1}
                                >
                                    <Row
                                        className="characteristic-pretension__result"
                                        arrangement="start"
                                    >
                                        {calculatedBolts}/{totalBolts} {t('common_bolts')}
                                    </Row>
                                    <Row
                                        className="characteristic-pretension__result"
                                        arrangement="center"
                                    >
                                        {minPretension ? (
                                            t(
                                                'common_min_unit_value',
                                                toDecimalPlaces(minPretension).toString(),
                                                t('common_unit_kn')
                                            )
                                        ) : (
                                            <PrimeIcon name="minus" />
                                        )}
                                    </Row>
                                    <Row
                                        className="characteristic-pretension__result"
                                        arrangement="end"
                                    >
                                        {averagePretension ? (
                                            t(
                                                'common_avg_unit_value',
                                                toDecimalPlaces(averagePretension).toString(),
                                                t('common_unit_kn')
                                            )
                                        ) : (
                                            <PrimeIcon name="minus" />
                                        )}
                                    </Row>
                                </Row>
                            </Column>
                        </Column>
                    </Card>
                </ContentColumn>
            </LayoutRowContainer>

            {allBolts.items.length > 0 && (
                <LayoutRowContainer>
                    <ContentColumn columns={3}>
                        <Card
                            className="pretension-graph-card"
                            title={
                                selectedBoltConnection?.hasValidPretensionRange
                                    ? t('bolt_connection_pretension_graph')
                                    : t('bolt_connection_invalid_pretension')
                            }
                        >
                            {selectedBoltConnection?.hasValidPretensionRange && (
                                <PretensionGraph
                                    onBoltClick={selectBolt}
                                    min={selectedBoltConnection.pretensionForceMin ?? 0}
                                    max={selectedBoltConnection.pretensionForceMax ?? 0}
                                    avg={boltDetails?.averagePretensionForceKn}
                                    bolts={allBolts.items}
                                />
                            )}
                        </Card>
                    </ContentColumn>
                </LayoutRowContainer>
            )}

            <LayoutRowContainer>
                <ContentColumn columns={3}>
                    <Card title={t('common_bolts')}>
                        <DataTable
                            value={bolts.items}
                            rowClassName={(b: Bolt) =>
                                b.isArchived ? 'archived-table-row' : undefined
                            }
                            rowHover={true}
                            emptyMessage={t('hardware_empty')}
                            onRowClick={navigateToBolt}
                            {...getSortableTableProps(bolts)}
                            {...getFilterableTableProps()}
                        >
                            <TableColumn
                                header={t('common_index')}
                                align="left"
                                body={(b: Bolt) => {
                                    const discrepancies = getBoltDiscrepancies(b)
                                    return (
                                        <Row className="index-table-column" gap={0.5}>
                                            <div className="index-table-column__index">
                                                {b.assignment?.position ?? (
                                                    <PrimeIcon name="minus" />
                                                )}
                                            </div>
                                            {discrepancies.length > 0 && (
                                                <TooltipIcon
                                                    iconClassName="index-table-column__discrepancy-icon"
                                                    target={b.id + '_discrepancies'}
                                                    name="exclamation-triangle"
                                                    label={discrepancies.join('\n')}
                                                />
                                            )}
                                        </Row>
                                    )
                                }}
                                {...getSortableColumnProps('assignment.index')}
                            />

                            <TableColumn
                                header={t('common_tag')}
                                body={(b: Bolt) => <TagIdCopyButton tag={b.currentTag} />}
                            />

                            <TableColumn
                                header={t('bolt_field_progress')}
                                body={(b: Bolt) => <BoltMeasurementProgressTooltipRow bolt={b} />}
                            />

                            <TableColumn
                                header={t('measurement_type_reference')}
                                body={(b: Bolt) =>
                                    b.activeReference ? (
                                        mm(toDecimalPlaces(b.activeReference.normalized.resultMm))
                                    ) : (
                                        <PrimeIcon name="minus" />
                                    )
                                }
                            />

                            <TableColumn
                                header={t('bolt_latest_elongation_header')}
                                body={(b: Bolt) =>
                                    b.latestElongation ? (
                                        mm(toDecimalPlaces(b.latestElongation.normalized.resultMm))
                                    ) : (
                                        <PrimeIcon name="minus" />
                                    )
                                }
                            />

                            <TableColumn
                                header={t('life_cycle_field_last_update_summary')}
                                body={(b: Bolt) => (
                                    <Row gap={0.5} alignment="center">
                                        <TooltipAvatar
                                            position="bottom"
                                            id={b.latestActivity?.user ?? ''}
                                            target={b.id + b.latestActivity?.user}
                                        />
                                        <BoltMeasurementProgressTooltipIcon
                                            type={b.latestActivity?.type ?? ''}
                                            target={b.id}
                                        />
                                        <DateDisplay value={b.latestActivity?.date} target={b.id} />
                                    </Row>
                                )}
                            />

                            {/* NOTE: The `assignedAt` column is disabled by client request. */}
                            {/* This is the only sortable date column. Instead, the index column solves the bolt-order sort problem. */}
                            {/* <TableColumn */}
                            {/*     header={t('bolt_field_assigned_at')} */}
                            {/*     body={(b: Bolt) => ( */}
                            {/*         <DateDisplay value={b.assignment?.assignedAt} target={b.id} /> */}
                            {/*     )} */}
                            {/*     {...getSortableColumnProps('assignment.assignedAt')} */}
                            {/* /> */}

                            {canWriteHardware && (
                                <TableColumn
                                    body={(b: Bolt) => (
                                        <TableActionContainer>
                                            {isArchived ? (
                                                <RestoreButton
                                                    target={b.id}
                                                    onClick={() => onRestoreBolt(b)}
                                                />
                                            ) : (
                                                <>
                                                    <UnlinkButton
                                                        target={b.id}
                                                        onClick={() => onUnassignBolt(b)}
                                                    />
                                                    <ArchiveButton
                                                        target={b.id}
                                                        onClick={() => onArchiveBolt(b)}
                                                    />
                                                </>
                                            )}
                                        </TableActionContainer>
                                    )}
                                />
                            )}
                        </DataTable>
                        <TablePagination data={bolts} />
                    </Card>
                </ContentColumn>
            </LayoutRowContainer>
        </StructureScreen>
    )
}
